import type { MatchLog, MatchSetup } from '@sd/bots';
import { BALANCE } from '@sd/content';
import type { Command, GameEvent, GameState } from '@sd/sim';
import type { Sample } from './history';

/**
 * Messages between the page and the simulation worker (#53). The worker
 * owns the match; the page only sends a person's commands and draws the
 * snapshots it gets back.
 */
export type ToWorker =
  | {
      readonly type: 'start';
      readonly setup: MatchSetup;
      /** Dev tool: play the match to this tick at once, for screenshots of later moments. */
      readonly skipTo?: number;
    }
  /** A network match (#139): the server's log so far; its steps follow as `steps`. */
  | { readonly type: 'remote'; readonly setup: MatchSetup; readonly log: MatchLog }
  | {
      readonly type: 'steps';
      readonly steps: readonly { readonly s: number; readonly c?: readonly Command[] }[];
    }
  | { readonly type: 'command'; readonly command: Command }
  /** Freezes time while a menu is open: only a match on one machine can do that. */
  | { readonly type: 'freeze'; readonly frozen: boolean }
  /** Ticks per real tick: 1 normally, more as a dev tool (#54). */
  | { readonly type: 'speed'; readonly speed: number }
  | { readonly type: 'log' };

export type FromWorker =
  | {
      readonly type: 'snapshot';
      readonly state: GameState;
      readonly events: readonly GameEvent[];
      /** Cycles played while skipping ahead (dev `at=`), so the end-of-match charts are whole. */
      readonly history?: readonly Sample[];
    }
  | { readonly type: 'log'; readonly log: MatchLog }
  | { readonly type: 'error'; readonly message: string };

/** Simulation ticks per second of real time. */
export const TICKS_PER_SECOND = 1000 / BALANCE.tickMs;
